// app/javascript/controllers/pane_stack_controller.js
import { Controller } from "@hotwired/stimulus";
import { PaneManager } from "../modules/pane_manager";
import { UrlManager } from "../modules/url_manager";
import { scrollToPane } from "../modules/ui_utils";

// Configuration class for initialization
class PaneStackConfig {
  constructor(element) {
    this.basePath = element.dataset.basePath;
    this.panePath = element.dataset.panePath;
    this.turboFramePrefix = element.dataset.turboFramePrefix || "pane_";
  }
}

// Helper class for event management
class EventRegistry {
  constructor() {
    this.listeners = [];
  }

  add(target, event, handler) {
    target.addEventListener(event, handler);
    this.listeners.push({ target, event, handler });
  }

  removeAll() {
    this.listeners.forEach(({ target, event, handler }) => {
      target.removeEventListener(event, handler);
    });
    this.listeners = [];
  }
}

export default class extends Controller {
  static targets = ["pane"];

  connect() {
    // this.element = <main id="pane-stack"...>
    const config = new PaneStackConfig(this.element);

    this.basePath = config.basePath;
    this.panePath = config.panePath;
    this.turboFramePrefix = config.turboFramePrefix;
    this.paneManager = new PaneManager(this.panePath, this.turboFramePrefix, this.element);
    this.urlManager = new UrlManager();
    this.registerEvents();
    this.restoreState();
  }

  disconnect() {
    this.unregisterEvents();
  }

  openPane(event) {
    event.preventDefault();

    if (!this.paneManager) {
      console.error("PaneManager not initialized in openPane");
      return;
    }

    const href = event.currentTarget.href;
    const slug = this.extractSlug(href);
    const currentPane = this.findCurrentPane(event.currentTarget);

    const existingPane = this.paneManager.findPaneBySlug(slug, currentPane);
    if (existingPane) {
      scrollToPane(existingPane);
      return;
    }

    const slugs = this.paneManager.openPane(slug, href, currentPane);
    this.urlManager.updateUrl(this.basePath, this.panePath, slugs);
  }

  // TODO: TEST CLOSE PANE
  closePane(event) {
    event.preventDefault();

    if (!this.paneManager) {
      console.error("PaneManager not initialized in closePane");
      return;
    }

    const currentPane = this.findCurrentPane(event.currentTarget);
    const slugs = this.paneManager.closePane(currentPane, this.paneManager.container);
    this.urlManager.updateUrl(this.basePath, this.panePath, slugs);
  }

  handleNewPane(event) {
    event.preventDefault();
    if (!this.paneManager) {
      console.error("PaneManager not initialized in handleNewPane");
      return;
    }
    const href = event.detail.url;
    const slug = this.extractSlug(href);

    const existingNewPane = this.paneManager.findPaneBySlug(slug, this.paneManager.container);
    if (existingNewPane) existingNewPane.remove();

    this.paneManager.appendPane(slug, href);
    const currentSlugs = this.paneManager.paneStack.getSlugs(this.element);
    this.urlManager.updateUrl(this.basePath, this.panePath, currentSlugs);
  }

  handlePaneUrlUpdate(event) {
    const newSlug = event.detail.zslug;
    const currentSlugs = this.urlManager.getSlugs();
    const updatedSlugs = currentSlugs.map(slug => (slug === "new" ? newSlug : slug));
    this.urlManager.updateUrl(this.basePath, this.panePath, updatedSlugs);
  }

  restoreState() {
    const slugs = this.urlManager.getSlugs();
    this.paneManager.clearPanes(["base_pane"]);
    slugs.forEach(slug => {
      if (!this.paneManager.findPaneBySlug(slug, this.paneManager.container)) {
        this.paneManager.appendPane(slug, this.paneManager.pahePath);
      }
    });
    if (slugs.length > 0) {
      this.urlManager.updateUrl(this.basePath, this.panePath, slugs);
    }
  }

  extractSlug(href) {
    const url = new URL(href, window.location.origin);
    const parts = url.pathname.split('/').filter(Boolean);
    return parts[parts.length - 1];
  }

  findCurrentPane(target) {
    return this.paneTargets.find(pane => pane.contains(target));
  }

  registerEvents() {
    this.eventRegistry = new EventRegistry();
    this.eventRegistry.add(window, "popstate", this.restoreState.bind(this));
    this.eventRegistry.add(this.element, "turbo:before-visit", this.beforeVisit.bind(this));
    this.eventRegistry.add(this.element, "turbo:frame-load", this.handleFrameLoad.bind(this));
    this.eventRegistry.add(document, "pane-stack:new", this.handleNewPane.bind(this));
    this.eventRegistry.add(document, "update-pane-url", this.handlePaneUrlUpdate.bind(this));
  }

  unregisterEvents() {
    this.eventRegistry.removeAll();
  }

  beforeVisit(event) {
    if (this.isPaneLink(event.target)) {
      this.openPane(event);
    }
  }

  handleFrameLoad(event) {
    const frame = event.target;
    scrollToPane(frame);
  }

  isPaneLink(link) {
    return link.tagName === "A" &&
           !event.defaultPrevented &&
           link.dataset.action?.includes("pane-stack#openPane");
  }
}
